// src/pages/ListingReviewPage.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Form, 
  ButtonGroup, 
  ToggleButton
} from 'react-bootstrap';
import FlippableListingCard from '../components/Profile/FlippableListingCard';
import { State, City } from 'country-state-city';
import './ListingReviewPage.css';

export default function ListingReviewPage() {
  const { auth } = useContext(AuthContext);
  const BASE = process.env.REACT_APP_RXROOSTER_BACKEND_URL;

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalFacilities, setTotalFacilities] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("pending"); // Default to pending
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const locationRef = useRef(null);

  // Fetch listings based on current review status
  useEffect(() => {
    // Only proceed if we have a valid token
    if (!auth.token) {
      console.log('Waiting for auth token to be available...');
      return;
    }
    
    setLoading(true);
    setError(null);
    // For the pending view, also show the regular listings endpoint as a fallback
    const endpoint = reviewStatus === 'pending' 
      ? `${BASE}/listings/${reviewStatus}?page=${page}&limit=${limit}`
      : `${BASE}/listings/${reviewStatus}?page=${page}&limit=${limit}`;
      
    console.log('Fetching listings with token:', auth.token ? 'token-available' : 'no-token');
    
    fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to fetch listings');
        return r.json();
      })
      .then(data => {
        // Log the API response to debug
        console.log('API response:', data);
        
        // Handle both formats: {listings, total} or direct array of listings
        let listingsArray = [];
        let totalCount = 0;
        
        if (Array.isArray(data)) {
          // If the response is a direct array of listings
          listingsArray = data;
          totalCount = data.length;
        } else if (data.listings && Array.isArray(data.listings)) {
          // If the response has a listings property with an array
          listingsArray = data.listings;
          totalCount = data.total || data.listings.length;
        } else {
          console.error('Unexpected API response format:', data);
          throw new Error('Unexpected API response format');
        }
        
        setTotalFacilities(totalCount);

        const flat = listingsArray.map(lst => {
          let facilityDetails = {};
          if (lst.facility && typeof lst.facility === 'object') {
            facilityDetails = {
              facilityType: lst.facility.facilityDetails?.facilityType || '',
              facilityName: lst.facility.name || '',
              location: lst.facility.location || '',
            };
          }
          return {
            ...lst,
            ...facilityDetails,
            assignmentType: Array.isArray(lst.assignmentType)
              ? lst.assignmentType
              : lst.assignmentType
              ? [lst.assignmentType]
              : [],
            title: lst.title ?? '',
            summary: lst.summary ?? '',
            description: lst.description ?? '',
            payAmount: lst.payAmount ?? null,
            payPeriod: lst.payPeriod ?? '',
            contractType: lst.contractType ?? '',
            startDate: lst.startDate ?? '',
            duration: lst.duration ?? '',
            scheduleType: lst.scheduleType ?? '',
            estimatedWeeklyHours: lst.estimatedWeeklyHours ?? null,
            clinicalExpectations: {
              requiredCaseTypes: lst.clinicalExpectations?.requiredCaseTypes ?? [],
              emrSystem: lst.clinicalExpectations?.emrSystem ?? '',
            },
            includesHousing: Boolean(lst.includesHousing),
            includesTravel: Boolean(lst.includesTravel),
            malpracticeCovered: Boolean(lst.malpracticeCovered),
            tags: lst.tags ?? [],
            reviewStatus: lst.reviewStatus || 'pending',
          };
        });
        
        setListings(flat);
        setCurrentIndex(0);
      })
      .catch(err => {
        setError(err.message);
        setListings([]);
        setTotalFacilities(0);
      })
      .finally(() => setLoading(false));
  }, [auth.token, page, limit, BASE, reviewStatus]);

  // Fetch location suggestions
  useEffect(() => {
    if (locationSearch.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    // Get all US states
    const usStates = State.getStatesOfCountry('US');
    let suggestions = [];
    const searchLower = locationSearch.toLowerCase();

    // Check if the search is in the format "City, State" (even partially)
    const cityStateMatch = searchLower.match(/^([^,]+),\s*(.*)$/);
    
    if (cityStateMatch) {
      // We have a city and potentially a partial state
      const cityPart = cityStateMatch[1].trim();
      const statePart = cityStateMatch[2].trim();
      
      if (statePart.length > 0) {
        // Find states matching the partial state part
        const matchingStates = usStates.filter(state => 
          state.name.toLowerCase().startsWith(statePart) || 
          state.isoCode.toLowerCase().startsWith(statePart)
        );
        
        // For each matching state, find matching cities
        matchingStates.forEach(state => {
          const cities = City.getCitiesOfState('US', state.isoCode);
          const matchingCities = cities.filter(city => 
            city.name.toLowerCase().includes(cityPart)
          );
          
          suggestions = [
            ...suggestions, 
            ...matchingCities.map(city => `${city.name}, ${state.isoCode}`)
          ];
        });
      } else {
        // Just city entered with comma, search all states
        usStates.forEach(state => {
          const cities = City.getCitiesOfState('US', state.isoCode);
          const matchingCities = cities.filter(city => 
            city.name.toLowerCase().includes(cityPart)
          ).slice(0, 1); // Limit to 1 match per state to avoid too many "Athens, XX"
          
          suggestions = [
            ...suggestions, 
            ...matchingCities.map(city => `${city.name}, ${state.isoCode}`)
          ];
        });
      }
    } else {
      // Standard search for state or city
      // First check if the search might be for a state (including partial matches)
      const matchingStates = usStates.filter(state => 
        state.name.toLowerCase().includes(searchLower) ||
        state.isoCode.toLowerCase().includes(searchLower)
      );

      // Also look for states where the search term appears within the state name
      // e.g., "Mexico" should match "New Mexico"
      const partialStateMatches = usStates.filter(state => 
        !matchingStates.includes(state) && 
        state.name.toLowerCase().split(' ').some(part => part.includes(searchLower))
      );
      
      const allMatchingStates = [...matchingStates, ...partialStateMatches];

      // For each matching state, get cities
      allMatchingStates.forEach(state => {
        const stateCities = City.getCitiesOfState('US', state.isoCode);
        const topCities = stateCities
          .slice(0, 5) // Get top 5 cities from each matching state
          .map(city => `${city.name}, ${state.isoCode}`);
        
        suggestions = [...suggestions, ...topCities];
      });

      // Also search directly for cities across all states
      if (suggestions.length < 10) {
        usStates.forEach(state => {
          if (suggestions.length >= 10) return;
          
          const stateCities = City.getCitiesOfState('US', state.isoCode);
          const matchingCities = stateCities
            .filter(city => city.name.toLowerCase().includes(searchLower))
            .slice(0, 2)
            .map(city => `${city.name}, ${state.isoCode}`);
          
          suggestions = [...suggestions, ...matchingCities].slice(0, 10);
        });
      }
    }

    setLocationSuggestions([...new Set(suggestions)].slice(0, 10)); // Remove duplicates and limit to 10
    
  }, [locationSearch]);

  // Handle clicking outside the location suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
        
        // Only clear the search if nothing was selected AND the user was actively searching
        // This prevents clearing when just clicking between form fields
        if (!locationSelected && showLocationSuggestions) {
          setLocationSearch('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [locationSelected, showLocationSuggestions]);

  const current = listings[currentIndex] || null;
  const canPrev = currentIndex > 0 || page > 1;
  const canNext = currentIndex < listings.length - 1 || (page * limit) < totalFacilities;

  useEffect(() => {
    if (current) {
      setLocationSearch(current.location || '');
      setLocationSelected(true);
    }
  }, [current]);

  // Sync locationSearch with current.location when it changes
  useEffect(() => {
    if (current && current.location && locationSearch === '') {
      setLocationSearch(current.location);
      setLocationSelected(true);
    }
  }, [current, locationSearch]);

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
    else setPage(p => p - 1);
  };
  const handleNext = () => {
    if (currentIndex < listings.length - 1) setCurrentIndex(i => i + 1);
    else setPage(p => p + 1);
  };

  const updateField = (field, value) => {
    if (current) {
      const updated = { ...current, [field]: value };
      
      // If pay period is changing, update the pay amount to a reasonable default in the new range
      if (field === 'payPeriod') {
        // If switching to annual and current pay is hourly-range
        if (value === 'annual' && current.payAmount < 1000) {
          updated.payAmount = getPayRange('annual').min;
        }
        // If switching to hourly and current pay is annual-range
        else if (value === 'hourly' && current.payAmount > 1000) {
          updated.payAmount = getPayRange('hourly').min;
        }
      }
      
      setListings(prev => prev.map((l, i) => (i === currentIndex ? updated : l)));
    }
  };

  const updateNested = (group, key, value) =>
    setListings(ls => ls.map((l, i) =>
      i === currentIndex
        ? { ...l, [group]: { ...l[group], [key]: value } }
        : l
    ));

  const requiredFields = [
    'estimatedWeeklyHours',
    'scheduleType',
    'assignmentType',
    'startDate',
    'contractType',
    'payPeriod',
    'payAmount',
    'location',
    'facilityType',
  ];

  const validateRequiredFields = (listing) => {
    const missing = [];
    if (!listing.estimatedWeeklyHours && listing.estimatedWeeklyHours !== 0) missing.push('Hours/Week');
    if (!listing.scheduleType) missing.push('Schedule Type');
    if (!listing.assignmentType || !Array.isArray(listing.assignmentType) || listing.assignmentType.length === 0) missing.push('Assignment Type');
    if (!listing.startDate) missing.push('Start Date');
    if (!listing.contractType) missing.push('Contract Type');
    if (!listing.payPeriod) missing.push('Pay Period');
    if (!listing.payAmount && listing.payAmount !== 0) missing.push('Pay Amount');
    if (!listing.location) missing.push('Location');
    if (!listing.facilityType) missing.push('Facility Type');
    return missing;
  };

  const saveListing = async () => {
    if (!current || saving) return;

    // Validate required fields
    const missingFields = validateRequiredFields(current);
    if (missingFields.length > 0) {
      setError(`Please fill out all required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { _id: id } = current;
      // Remove fields that should not be sent to the API
      const { _id, facility, v, reviewStatus, createdAt, updatedAt, ...data } = current;

      // If facility is an object, update its fields with the latest values
      let facilityPayload = null;
      if (facility && typeof facility === 'object') {
        facilityPayload = {
          ...facility,
          facilityType: facility.facilityDetails?.facilityType,
          name: facility.name,
          location: facility.location,
        };
      }

      // Remove top-level facility fields if facility object exists
      if (facilityPayload) {
        delete data.facilityType;
        delete data.facilityName;
        delete data.location;
      }

      // Compose payload
      const payload = {
        ...data,
        ...(facilityPayload ? { facility: facilityPayload } : {
          facilityType: facility.facilityDetails?.facilityType,
          facilityName: facility.facilityName,
          location: facility.location,
        })
      };

      console.log('Saving listing with data:', payload);

      // Update the listing data
      const updateRes = await fetch(`${BASE}/listings/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!updateRes.ok) throw new Error('Failed to update listing');

      // Explicitly mark as reviewed
      const reviewRes = await fetch(`${BASE}/listings/${id}/review-status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'reviewed' }),
      });

      if (!reviewRes.ok) throw new Error('Failed to update review status');

      // Remove from current listings and go to next
      setListings(prev => prev.filter(l => l._id !== id));
      // toast.success('Listing saved and marked as reviewed!');
    } catch (err) {
      console.error(err);
      setError(err.message);
      // toast.error(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Pre-defined options for dropdown and button groups
  const payPeriodOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'annual', label: 'Annually' },
  ];

  const contractTypeOptions = [
    { value: 'W2', label: 'W-2 Employee' },
    { value: '1099', label: '1099 Contractor' },
  ];

  const assignmentTypeOptions = [
    { value: 'PRN', label: 'PRN (Per Diem)' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Locum tenens', label: 'Locum Tenens' },
    { value: 'Permanent', label: 'Permanent' },
  ];

  const scheduleTypeOptions = [
    { value: 'Weekday', label: 'Weekday' },
    { value: 'Weekend', label: 'Weekend' },
    { value: 'Nights', label: 'Nights' },
    { value: 'Rotating', label: 'Rotating' },
    { value: 'Flexible', label: 'Flexible' },
  ];

  const emrOptions = [
    { value: 'Epic', label: 'Epic' },
    { value: 'Cerner', label: 'Cerner' },
    { value: 'Allscripts', label: 'Allscripts' },
    { value: 'Meditech', label: 'Meditech' },
    { value: 'Other', label: 'Other' },
  ];

  // Helper function for rendering toggle buttons
  const renderToggleButtonGroup = (
    name,
    options,
    selectedValue,
    onChange,
    multiple = false
  ) => (
    <ButtonGroup className="d-flex flex-wrap mb-3">
      {options.map((option, idx) => {
        const uniqueId = `${name}_${option.value}_${idx}`;
        const checked = multiple 
          ? (selectedValue || []).includes(option.value)
          : selectedValue === option.value;
        
        return (
          <ToggleButton
            key={uniqueId}
            id={uniqueId}
            type={multiple ? "checkbox" : "radio"}
            variant="outline-primary"
            name={name}
            value={option.value}
            checked={checked}
            onChange={(e) => onChange(name, multiple ? [...(selectedValue || []).filter(v => v !== option.value), ...(e.target.checked ? [option.value] : [])] : e.target.value)}
            className="m-1"
          >
            {option.label}
          </ToggleButton>
        );
      })}
    </ButtonGroup>
  );

  const getPayRange = (payPeriod) => {
    switch (payPeriod) {
      case 'hourly':
        return { min: 80, max: 400, step: 5 };
      case 'annual':
        return { min: 150000, max: 800000, step: 10000 };
      default:
        return { min: 80, max: 400, step: 5 };
    }
  };

  if (loading) return (
    <div className="loading">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );

  if (error) return (
    <div className="error">
      <p>{error}</p>
      <button onClick={() => setError(null)}>Try Again</button>
    </div>
  );

  if (!current && !loading && listings.length === 0) return (
    <div className="all-done">
      <h2>All Done! 🎉</h2>
      <p>No more {reviewStatus} listings to review.</p>
      {!reviewStatus || reviewStatus === 'pending' ? (
        <button onClick={() => setReviewStatus('reviewed')}>View Reviewed Listings</button>
      ) : (
        <button onClick={() => setReviewStatus('pending')}>Back to Pending Listings</button>
      )}
    </div>
  );

  if (!current) return (
    <div className="no-listings">
      <p>No listings on this page.</p>
      <button onClick={() => setPage(1)}>Go to first page</button>
    </div>
  );

  return (
    <div className="listing-review-page">
      <h1>Review &amp; Edit Facility Listing</h1>

      <div className="review-status-filter">
        <div 
          className={`status-badge pending ${reviewStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setReviewStatus('pending')}
        >
          <span className="count">{reviewStatus === 'pending' ? (totalFacilities || 0) : '?'}</span> Pending
        </div>
        <div 
          className={`status-badge reviewed ${reviewStatus === 'reviewed' ? 'active' : ''}`}
          onClick={() => setReviewStatus('reviewed')}
        >
          <span className="count">{reviewStatus === 'reviewed' ? (totalFacilities || 0) : '?'}</span> Reviewed
        </div>
      </div>

      <div className="review-controls">
        <button
          onClick={handlePrev}
          disabled={!canPrev || saving}
          className={saving ? 'disabled' : ''}
        >
          ← Prev
        </button>
        <span>
          {((page - 1) * limit + currentIndex + 1)} of {Math.max(listings.length, totalFacilities)}
        </span>
        <button
          onClick={handleNext}
          disabled={!canNext || saving}
          className={saving ? 'disabled' : ''}
        >
          Next →
        </button>
      </div>

      <div className="review-content">
        <div className="form-container">
          <form onSubmit={e => { e.preventDefault(); saveListing().catch(alert); }}>

            {/* Facility Details */}
            <fieldset className="mb-4">
              <legend>Facility Details</legend>
              
              <label>Facility Name</label>
              <input
                type="text"
                placeholder="–"
                value={current.facilityName ?? ''}
                onChange={e => updateField('facilityName', e.target.value)}
                className="form-control mb-3"
              />
              
              <label>Facility Type</label>
              <div className="tag-selector mb-3">
                {['Hospital', 'Surgery Center', 'Clinic', 'Office-Based', 'Pain Clinic', 'University'].map(type => (
                  <div 
                    key={type} 
                    className={`tag-pill ${current.facilityDetails?.facilityType === type ? 'selected' : ''}`}
                    onClick={() => {
                      updateField('facilityType', type);
                    }}
                  >
                    {type}
                  </div>
                ))}
              </div>
              
              <label>Location</label>
              <div className="location-autocomplete" ref={locationRef}>
                <input
                  type="text"
                  placeholder="City, State"
                  value={locationSearch}
                  onChange={e => {
                    setLocationSearch(e.target.value);
                    setShowLocationSuggestions(true);
                    setLocationSelected(false);
                    // Allow direct update of location field as user types
                    updateField('location', e.target.value);
                  }}
                  onBlur={() => {
                    // On blur, if the locationSearch is different from current.location, update it
                    if (locationSearch !== current.location) {
                      updateField('location', locationSearch);
                    }
                  }}
                  onFocus={() => setShowLocationSuggestions(true)}
                  className="form-control mb-0"
                />
                {showLocationSuggestions && locationSuggestions.length > 0 && (
                  <ul className="location-suggestions">
                    {locationSuggestions.map((location, index) => (
                      <li 
                        key={index} 
                        onClick={() => {
                          updateField('location', location);
                          setLocationSearch(location);
                          setShowLocationSuggestions(false);
                          setLocationSelected(true);
                        }}
                      >
                        {location}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </fieldset>

            {/* Title, Summary, Description */}
            <fieldset className="mb-4">
              <legend>Title, Summary, Description</legend>
              <label>Title</label>
              <input
                type="text"
                placeholder="–"
                value={current.title ?? ''}
                onChange={e => updateField('title', e.target.value)}
                className="form-control mb-3"
              />

              <label>Summary</label>
              <textarea
                placeholder="–"
                value={current.summary ?? ''}
                onChange={e => updateField('summary', e.target.value)}
                className="form-control mb-3"
                rows={2}
              />

              <label>Description</label>
              <textarea
                placeholder="–"
                value={current.description ?? ''}
                onChange={e => updateField('description', e.target.value)}
                className="form-control mb-3"
                rows={3}
              />
            </fieldset>

            {/* Rate & Contract */}
            <fieldset className="mb-4">
              <legend>Rate & Contract</legend>
              
              <label>Pay Amount</label>
              <Form.Range
                min={getPayRange(current.payPeriod || 'hourly').min}
                max={getPayRange(current.payPeriod || 'hourly').max}
                step={getPayRange(current.payPeriod || 'hourly').step}
                value={current.payAmount ?? getPayRange(current.payPeriod || 'hourly').min}
                onChange={e => updateField('payAmount', parseInt(e.target.value, 10))}
                className="mb-3"
              />
              <div className="range-value">
                {current.payPeriod === 'annual' 
                  ? `${Math.round((current.payAmount || getPayRange('annual').min) / 1000)}K/yr` 
                  : `${current.payAmount || getPayRange('hourly').min}/hr`}
              </div>

              <label>Pay Period</label>
              {renderToggleButtonGroup(
                'payPeriod',
                payPeriodOptions,
                current.payPeriod || '',
                (name, value) => updateField(name, value)
              )}

              <label>Contract Type</label>
              {renderToggleButtonGroup(
                'contractType',
                contractTypeOptions,
                current.contractType || '',
                (name, value) => updateField(name, value)
              )}
            </fieldset>

            {/* Coverage Needs */}
            <fieldset className="mb-4">
              <legend>Coverage Needs</legend>
              
              <label>Start Date</label>
              <Form.Control
                type="date"
                value={current.startDate ?? ''}
                onChange={e => updateField('startDate', e.target.value)}
                className="mb-3"
              />

             
              <label>Schedule Type</label>
              {renderToggleButtonGroup(
                'scheduleType',
                scheduleTypeOptions,
                current.scheduleType || '',
                (name, value) => updateField(name, value)
              )}

              <label>Hours/Week</label>
              <Form.Range
                min={0}
                max={80}
                step={4}
                value={current.estimatedWeeklyHours ?? 0}
                onChange={e => updateField('estimatedWeeklyHours', parseInt(e.target.value, 10))}
                className="mb-3"
              />
              <div className="range-value">{current.estimatedWeeklyHours ?? 0} hours</div>
               <div className="tag-selector mb-3">
                {assignmentTypeOptions.map(opt => (
                  <div
                    key={opt.value}
                    className={`tag-pill ${current.assignmentType && current.assignmentType.includes(opt.value) ? 'selected' : ''}`}
                    onClick={() => {
                      let updated = Array.isArray(current.assignmentType) ? [...current.assignmentType] : [];
                      if (updated.includes(opt.value)) {
                        updated = updated.filter(v => v !== opt.value);
                      } else {
                        updated.push(opt.value);
                      }
                      updateField('assignmentType', updated);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>

            </fieldset>

            {/* Clinical Expectations */}
            <fieldset className="mb-4">
              <legend>Clinical Expectations</legend>
              
              <label>Required Case Types</label>
              <div className="tag-selector mb-3">
                {['General', 'Cardiac', 'Pediatric', 'Regional', 'OB', 'Trauma', 'Pain', 'Critical Care'].map(tag => (
                  <div 
                    key={tag} 
                    className={`tag-pill ${
                      (current.clinicalExpectations?.requiredCaseTypes || []).includes(tag) 
                        ? 'selected' 
                        : ''
                    }`}
                    onClick={() => {
                      // Define valid case types
                      const validCaseTypes = ['General', 'Cardiac', 'Pediatric', 'Regional', 'OB', 'Trauma', 'Pain', 'Critical Care'];
                      
                      // Get current types, but filter out any invalid ones first
                      const currentTypes = (current.clinicalExpectations?.requiredCaseTypes || [])
                        .filter(type => validCaseTypes.includes(type));
                      
                      // Toggle the selected tag
                      const newTypes = currentTypes.includes(tag)
                        ? currentTypes.filter(t => t !== tag)
                        : [...currentTypes, tag];
                      
                      updateField('clinicalExpectations', {
                        ...current.clinicalExpectations,
                        requiredCaseTypes: newTypes
                      });
                    }}
                  >
                    {tag}
                  </div>
                ))}
              </div>

              <label>EMR System</label>
              {renderToggleButtonGroup(
                'emrSystem',
                emrOptions,
                current.clinicalExpectations?.emrSystem || '',
                (name, value) => updateNested('clinicalExpectations', 'emrSystem', value)
              )}

            </fieldset>

            {/* Perks */}
            <fieldset className="mb-4">
              <legend>Perks</legend>
              <div className="perks-container">
                <Form.Check
                  type="switch"
                  id="housing-switch"
                  label="Housing"
                  checked={current.includesHousing || false}
                  onChange={e => updateField('includesHousing', e.target.checked)}
                  className="perk-switch"
                />
                <Form.Check
                  type="switch"
                  id="travel-switch"
                  label="Travel"
                  checked={current.includesTravel || false}
                  onChange={e => updateField('includesTravel', e.target.checked)}
                  className="perk-switch"
                />
                <Form.Check
                  type="switch"
                  id="malpractice-switch"
                  label="Malpractice Covered"
                  checked={current.malpracticeCovered || false}
                  onChange={e => updateField('malpracticeCovered', e.target.checked)}
                  className="perk-switch"
                />
              </div>
            </fieldset>

            {/* Tags */}
            <fieldset className="mb-4">
              <legend>Tags</legend>
              <div className="tag-selector mb-3">
                {['High-paying', 'Rural', 'Urban', 'Teaching', 'Community', 'Academic', 'Government', 'Private'].map(tag => (
                  <div 
                    key={tag} 
                    className={`tag-pill ${(current.tags || []).includes(tag) ? 'selected' : ''}`}
                    onClick={() => {
                      const currentTags = current.tags || [];
                      updateField('tags', 
                        currentTags.includes(tag)
                          ? currentTags.filter(t => t !== tag)
                          : [...currentTags, tag]
                      );
                    }}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={saving}
              className={`save-button ${saving ? 'disabled' : ''}`}
            >
              {saving ? 'Saving...' : 'Save Listing'}
            </button>
          </form>
        </div>

        <div className="deck-wrapper deck-wrapper-listing-review">
          <FlippableListingCard profile={current} isCertified={false} />
        </div>
      </div>
    </div>
  );
}
